import React, {
    useMemo,
    useContext,
    useState,
    useCallback,
    useEffect,
} from "react";
import { useAsync, usePrevious } from "hooks";
import { fetchItemContext } from "data/api";
import { ItemContextKeyEnum } from "data/types";
import { getDisplayName } from "data/constants";
import { ItemContext } from "data/context";

type Config = {
    asProps?: boolean | Array<ItemContextKeyEnum>,
};

type Props = {
    itemUri: string,
    refreshId: ?number,
};

const withItemContext =
    ({ asProps }: ?Config = {}) =>
    (WrappedComponent: ComponentType<any>) => {
        const HOC = (props: Props) => {
            const parentContext = useContext(ItemContext);
            const itemUri = props.itemUri;
            const [refreshId, setRefreshId] = useState(props.refreshId || 0);
            const prevRefreshId = usePrevious(refreshId) ?? refreshId;

            // #region Nested context update by refreshId
            // Update refreshId if the nested child's itemContext refreshId prop changes

            const baseRefreshContext = useCallback(
                (newRefreshId) => {
                    /* istanbul ignore if*/
                    if (refreshId === newRefreshId) {
                        return;
                    }
                    setRefreshId(newRefreshId);
                },
                [refreshId]
            );

            // get the parentContext's refreshContext,
            // if parentContext is available and itemUris match,
            // otherwise set it
            const refreshContext =
                parentContext?.itemUri === itemUri
                    ? parentContext?.refreshContext ?? baseRefreshContext
                    : baseRefreshContext;

            useEffect(() => {
                setRefreshId(props.refreshId);
            }, [props.refreshId]);

            // if the refreshId changed, update the parent itemContext's refreshId as well
            useEffect(() => {
                if (prevRefreshId === refreshId) {
                    return;
                }
                refreshContext(refreshId);
            }, [refreshId, prevRefreshId, refreshContext]);
            // #endregion

            const [itemContext, setItemContext] = useState({});

            useAsync(async () => {
                if (!itemUri) return setItemContext({});
                if (parentContext?.itemUri === itemUri)
                    return setItemContext(parentContext);
                setItemContext({ itemUri });
                let c = {};
                try {
                    c = await fetchItemContext(itemUri);
                    c.itemUri = itemUri;
                } catch (e) {
                    console.error(e);
                }
                setItemContext(c);
            }, [parentContext, itemUri, refreshId]);

            const itemContextWithExtras = useMemo(
                () => ({
                    itemUri,
                    ...itemContext,
                    refreshContext,
                }),
                [itemContext, itemUri, refreshContext]
            );

            const itemContextAsProps =
                asProps === true ? itemContextWithExtras : {};
            if (asProps && Array.isArray(asProps)) {
                asProps.forEach((key) => {
                    itemContextAsProps[key] = itemContextWithExtras[key];
                });
            }

            return (
                <ItemContext.Provider value={itemContextWithExtras}>
                    <WrappedComponent {...props} {...itemContextAsProps} />
                </ItemContext.Provider>
            );
        };
        HOC.displayName = `withItemContext(${getDisplayName(
            WrappedComponent
        )})`;
        return HOC;
    };

export default withItemContext;
